/**
 * The search-and-filter bar above a list table. The search box always shows; from md up the other
 * controls sit beside it, and on a phone they fold behind a "Filters" button (with how many are
 * set), so the bar stays one line and the table keeps most of the screen.
 *
 * The controls below (search, select, sort, batch size, clear) are the bar's usual contents, so
 * every table's bar looks and behaves the same.
 */

import { useId, useState } from "react";
import type { ReactNode } from "react";

import { Select } from "./Select";

export function FilterBar({
  label,
  search,
  narrowing,
  children,
}: {
  /** The bar's accessible name. */
  label: string;
  /** The search control, always in view. */
  search: ReactNode;
  /** How many filters (besides the search) are set: shown on the phone's Filters button. */
  narrowing: number;
  /** The other controls. */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <section aria-label={label} className="shrink-0 rounded-xl border bg-white p-3 text-sm shadow-xs">
      <div className="flex flex-wrap items-end gap-3">
        {search}
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={id}
          className={`rounded-lg border px-3 py-1.5 md:hidden ${narrowing ? "border-brand text-brand" : "text-gray-600"}`}
        >
          Filters{narrowing ? ` (${narrowing})` : ""}
        </button>
        <div id={id} className={`${open ? "grid" : "hidden"} w-full grid-cols-2 items-end gap-3 md:contents`}>
          {children}
        </div>
      </div>
    </section>
  );
}

/** A labelled control in the bar. */
export function FilterField({ label, className = "", children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-gray-500 ${className}`}>
      {label}
      {children}
    </label>
  );
}

/** The search box, for FilterBar's `search`. */
export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder: string }) {
  return (
    <FilterField label="Search" className="min-w-[180px] flex-1">
      <div className="relative">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-lg border py-1.5 pl-11 pr-2 outline-hidden focus:border-brand focus:ring-1 focus:ring-brand"
        />
      </div>
    </FilterField>
  );
}

/** A filter picked from a list; an option with value "" means "any". */
export function SelectFilter<V extends string>({
  label,
  value,
  options,
  onChange,
  testId,
}: {
  label: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (value: V) => void;
  testId?: string;
}) {
  return (
    <FilterField label={label}>
      <Select value={value} options={options} onChange={onChange} data-testid={testId} />
    </FilterField>
  );
}

/** What the list is sorted by, and which way. */
export function SortFilter<K extends string>({
  value,
  options,
  descending,
  onChange,
  onToggle,
}: {
  value: K;
  options: readonly { value: K; label: string }[];
  descending: boolean;
  onChange: (value: K) => void;
  onToggle: () => void;
}) {
  return (
    <FilterField label="Sort">
      <div className="flex">
        <Select value={value} options={options} onChange={onChange} className="flex-1 rounded-r-none" />
        <button
          type="button"
          onClick={onToggle}
          title={descending ? "Descending — click to reverse" : "Ascending — click to reverse"}
          aria-label={descending ? "Sorted descending. Click to sort ascending." : "Sorted ascending. Click to sort descending."}
          className="select-addon"
        >
          {descending ? "↓" : "↑"}
        </button>
      </div>
    </FilterField>
  );
}

const BATCH_SIZES = [25, 50, 100].map((n) => ({ value: n, label: String(n) }));

/** How many rows each page (batch) brings. */
export function BatchSizeFilter({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <FilterField label="Batch size">
      <Select value={value} options={BATCH_SIZES} onChange={onChange} />
    </FilterField>
  );
}

/** Clears the search and filters; shown while any is set (`count` of them). */
export function ClearFilters({ count, onClear }: { count: number; onClear: () => void }) {
  if (count === 0) return null;
  return (
    <button type="button" onClick={onClear} className="ml-auto self-end rounded-lg px-3 py-1.5 text-gray-500 hover:bg-gray-100">
      Clear filters ({count})
    </button>
  );
}
